import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mocks as mocksApi } from '../api';
import Navbar from '../components/Navbar';
import MathText from '../components/MathText';

/**
 * Mock exam sitting screen (Chunk 52; backend Chunk 50).
 *
 * Route: /mock/:assignmentId
 *
 * The backend is the invigilator. This page only:
 *   - starts or resumes the attempt (POST /start — the paper is sampled once)
 *   - shows the CURRENT section's questions and a countdown derived from
 *     the server's seconds_remaining (wall-clock deadline, not a decrement,
 *     so background tabs cannot stretch it — same as Chunk 46)
 *   - saves each answer immediately (POST /answer)
 *   - closes a section on "Next section" (POST /next); the last one submits
 *   - re-syncs with the server when the deadline passes or the tab returns
 *
 * No hints, no explanations, no ⇄ language toggle, no going back — exactly
 * like the real sitting. Scores on the final screen are RAW percentages and
 * say so; nothing here is a Qiyas score.
 *
 * No curly apostrophes in this file (Vercel ESLint).
 */

const MOCK_CSS = `
  .mk-wrap { max-width: 860px; margin: 0 auto; }
  .mk-bar { position: sticky; top: 0; z-index: 20; background: var(--bg-page, #F5F2EC); padding: 10px 0 8px; border-bottom: 1px solid var(--border); margin-bottom: 16px; }
  .mk-bar-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
  .mk-section { font-weight: 800; font-size: 1.05rem; color: var(--text-primary); }
  .mk-sub { font-size: 0.82rem; color: var(--text-muted); }
  .mk-clock { margin-inline-start: auto; font-variant-numeric: tabular-nums; font-feature-settings: "tnum"; font-weight: 800; font-size: 1.25rem; padding: 4px 14px; border-radius: 10px; background: rgba(11,93,75,0.08); color: var(--brand-green, #0B5D4B); }
  .mk-clock.warn { background: rgba(198,168,91,0.18); color: #8A6E2A; }
  .mk-clock.danger { background: rgba(185,28,28,0.10); color: #B91C1C; animation: mk-pulse 1s ease-in-out infinite; }
  @keyframes mk-pulse { 50% { opacity: 0.55; } }
  .mk-progress { height: 6px; border-radius: 3px; background: var(--brand-grey, #D6DBE0); margin-top: 8px; overflow: hidden; }
  .mk-progress > div { height: 100%; background: var(--brand-green, #0B5D4B); transition: width 1s linear; }
  .mk-nav { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
  .mk-dot { width: 30px; height: 30px; border-radius: 50%; border: 1.5px solid var(--border); background: #fff; font-size: 0.78rem; font-weight: 700; cursor: pointer; color: var(--text-muted); display: flex; align-items: center; justify-content: center; }
  .mk-dot.answered { background: rgba(22,163,74,0.14); border-color: rgba(22,163,74,0.5); color: #15803D; }
  .mk-dot.current { border: 2.5px solid var(--brand-navy, #1C2733); color: var(--text-primary); }
  .mk-card { background: #fff; border: 1px solid var(--border); border-radius: 14px; padding: 22px 22px 18px; }
  .mk-figure { width: 100%; height: clamp(120px, 28vh, 260px); display: flex; align-items: center; justify-content: center; margin-bottom: 14px; border-radius: 8px; border: 1px solid var(--border); background: #fff; overflow: hidden; }
  .mk-figure img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
  .mk-q { font-size: 1.05rem; line-height: 1.7; color: var(--text-primary); margin-bottom: 18px; }
  .mk-opt { display: flex; align-items: flex-start; gap: 14px; padding: 13px 16px; border-radius: 10px; border: 1.5px solid var(--border); background: #fff; cursor: pointer; margin-bottom: 10px; transition: border-color 0.12s, background 0.12s; }
  .mk-opt:hover { border-color: rgba(11,93,75,0.45); background: rgba(11,93,75,0.04); }
  .mk-opt.selected { border-color: var(--brand-green, #0B5D4B); background: rgba(11,93,75,0.08); }
  .mk-key { width: 28px; height: 28px; border-radius: 6px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 0.82rem; border: 1.5px solid var(--border); color: var(--text-muted); }
  .mk-opt.selected .mk-key { background: var(--brand-green, #0B5D4B); border-color: var(--brand-green, #0B5D4B); color: #fff; }
  .mk-opt-text { flex: 1; font-size: 0.95rem; line-height: 1.55; color: var(--text-secondary); padding-top: 3px; }
  .mk-foot { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 16px; }
  .mk-foot .spacer { flex: 1; }
  .mk-unanswered { font-size: 0.8rem; color: #8A6E2A; font-weight: 700; }
  .mk-result { background: #fff; border: 1px solid var(--border); border-radius: 14px; padding: 28px 24px; text-align: center; }
  .mk-result-big { font-size: 3.2rem; font-weight: 800; color: var(--brand-green, #0B5D4B); line-height: 1; }
  .mk-result-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 12px; margin: 22px 0; }
  .mk-result-tile { padding: 12px 8px; border-radius: 10px; background: var(--brand-sand, #F5F2EC); border: 1px solid var(--border); }
  .mk-result-tile b { display: block; font-size: 1.3rem; color: var(--text-primary); }
  .mk-result-tile span { font-size: 0.74rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.4px; }
  .mk-note { font-size: 0.8rem; color: var(--text-muted); line-height: 1.5; }
  .mk-intro { background: #fff; border: 1px solid var(--border); border-radius: 14px; padding: 26px 24px; }
  .mk-intro ul { margin: 12px 0 18px; padding-inline-start: 20px; color: var(--text-secondary); line-height: 1.7; }
  .mk-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px; }
  .mk-dialog { background: var(--bg-page, #F5F2EC); border-radius: 14px; padding: 22px; max-width: 460px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
`;

function fmtClock(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

const LETTERS = ['a', 'b', 'c', 'd'];

// ── Result screen ─────────────────────────────────────────────────────────────

function ResultScreen({ view, ar }) {
  const t = view.attempt;
  const a = view.assignment;
  const pct = (x) => (x && x.total ? Math.round((100 * x.correct) / x.total) : null);
  const area = t.area_scores || {};
  return (
    <div className="mk-result">
      <div className="mk-sub" style={{ marginBottom: 8 }}>{a.title}</div>
      <div className="mk-result-big">{t.pct == null ? '-' : `${Math.round(t.pct)}%`}</div>
      <div className="mk-sub" style={{ marginTop: 6 }}>
        {ar ? `${t.score} من ${t.total} إجابة صحيحة` : `${t.score} of ${t.total} correct`}
      </div>
      <div className="mk-result-grid">
        <div className="mk-result-tile"><b>{pct(area.verbal) == null ? '-' : `${pct(area.verbal)}%`}</b><span>{ar ? 'لفظي' : 'Verbal'}</span></div>
        <div className="mk-result-tile"><b>{pct(area.quant) == null ? '-' : `${pct(area.quant)}%`}</b><span>{ar ? 'كمي' : 'Quantitative'}</span></div>
        {(t.section_scores || []).map((sc, i) => (
          <div key={i} className="mk-result-tile"><b>{pct(sc) == null ? '-' : `${pct(sc)}%`}</b><span>{ar ? `القسم ${i + 1}` : `Section ${i + 1}`}</span></div>
        ))}
      </div>
      <p className="mk-note">
        {ar
          ? 'هذه نسبة الإجابات الصحيحة فقط، وليست درجة قياس (درجات قياس معيارية من 100).'
          : 'This is the raw percentage of correct answers, not a Qiyas score (Qiyas results are standardised out of 100).'}
      </p>
      <Link to="/dashboard" className="btn btn-primary" style={{ marginTop: 18, display: 'inline-flex' }}>
        {ar ? 'العودة إلى لوحتي' : 'Back to dashboard'}
      </Link>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MockExam() {
  const { i18n } = useTranslation();
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const ar = i18n.language === 'ar';

  const [view, setView] = useState(null);          // backend _attempt_view
  const [error, setError] = useState(null);
  const [started, setStarted] = useState(false);   // past the intro screen
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [confirmNext, setConfirmNext] = useState(false);
  const deadlineRef = useRef(null);
  const syncingRef = useRef(false);

  const applyView = useCallback((v) => {
    setView(v);
    setAnswers(v.answers || {});
    if (v.attempt?.status === 'in_progress') {
      deadlineRef.current = Date.now() + (v.seconds_remaining || 0) * 1000;
      setTimeLeft(v.seconds_remaining || 0);
      setIdx(0);
    }
  }, []);

  const start = useCallback(async () => {
    setBusy(true);
    try {
      const v = await mocksApi.start(assignmentId);
      applyView(v);
      setStarted(true);
    } catch (e) {
      if (e?.status === 409 && e?.error?.details?.attempt) {
        // Already submitted: show the result by loading the attempt.
        try { applyView(await mocksApi.attempt(e.error.details.attempt.id)); setStarted(true); }
        catch (e2) { setError(e2?.error?.message || 'Could not load.'); }
      } else {
        setError(e?.error?.message || 'Could not start the mock exam.');
      }
    } finally {
      setBusy(false);
    }
  }, [assignmentId, applyView]);

  // Re-sync with the server (deadline passed, tab returned, etc.).
  const resync = useCallback(async () => {
    if (!view?.attempt?.id || syncingRef.current) return;
    syncingRef.current = true;
    try {
      const v = await mocksApi.attempt(view.attempt.id);
      if (v.attempt.status !== 'in_progress' || v.section_index !== view.section_index) applyView(v);
      else {
        deadlineRef.current = Date.now() + (v.seconds_remaining || 0) * 1000;
        setTimeLeft(v.seconds_remaining || 0);
      }
    } catch { /* keep current view */ }
    finally { syncingRef.current = false; }
  }, [view, applyView]);

  // Wall-clock countdown. When it hits zero the server decides what happens
  // next (closes the section or submits) — we just ask it.
  useEffect(() => {
    if (!started || !view || view.attempt?.status !== 'in_progress') return undefined;
    const tick = () => {
      const left = deadlineRef.current ? Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)) : 0;
      setTimeLeft(left);
      if (left <= 0) resync();
    };
    const id = setInterval(tick, 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') { tick(); resync(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [started, view, resync]);

  const choose = async (qid, letter) => {
    const prev = answers[String(qid)];
    const next = prev === letter ? null : letter;
    setAnswers((a) => {
      const c = { ...a };
      if (next === null) delete c[String(qid)]; else c[String(qid)] = next;
      return c;
    });
    try {
      await mocksApi.answer(view.attempt.id, qid, next);
    } catch (e) {
      if (e?.status === 403 || e?.status === 409) resync();   // section closed meanwhile
    }
  };

  const nextSection = async () => {
    setConfirmNext(false);
    setBusy(true);
    try { applyView(await mocksApi.next(view.attempt.id)); window.scrollTo(0, 0); }
    catch (e) { setError(e?.error?.message || 'Could not continue.'); }
    finally { setBusy(false); }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <>
        <Navbar />
        <div className="page mk-wrap">
          <div className="card" style={{ padding: 24 }}>
            <div style={{ color: '#B91C1C', fontWeight: 700, marginBottom: 12 }}>{error}</div>
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/dashboard')}>{ar ? 'العودة' : 'Back'}</button>
          </div>
        </div>
      </>
    );
  }

  if (!started) {
    return (
      <>
        <Navbar />
        <div className="page mk-wrap">
          <style>{MOCK_CSS}</style>
          <div className="mk-intro" dir={ar ? 'rtl' : 'ltr'}>
            <h1 className="page-title" style={{ marginBottom: 6 }}>{ar ? 'اختبار تجريبي' : 'Mock exam'}</h1>
            <p className="page-subtitle">{ar ? 'قبل أن تبدأ:' : 'Before you start:'}</p>
            <ul>
              <li>{ar ? 'الاختبار مقسم إلى أقسام مؤقتة. عندما ينتهي وقت القسم أو تنتقل للقسم التالي، لا يمكنك الرجوع.' : 'The paper is split into timed sections. When a section ends or you move on, you cannot go back.'}</li>
              <li>{ar ? 'لديك محاولة واحدة فقط. إذا أغلقت الصفحة يستمر العداد.' : 'You have one attempt. If you close the page, the clock keeps running.'}</li>
              <li>{ar ? 'لا تلميحات ولا تبديل لغة أثناء الاختبار.' : 'No hints and no language toggle during the exam.'}</li>
              <li>{ar ? 'تُحفظ إجاباتك تلقائيًا عند اختيارها.' : 'Answers are saved the moment you pick them.'}</li>
            </ul>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn btn-primary" onClick={start} disabled={busy}>
                {busy ? '...' : (ar ? 'ابدأ الاختبار' : 'Start the exam')}
              </button>
              <Link to="/dashboard" className="btn btn-ghost">{ar ? 'ليس الآن' : 'Not now'}</Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!view) return null;

  if (view.attempt.status !== 'in_progress') {
    return (
      <>
        <Navbar />
        <div className="page mk-wrap" dir={ar ? 'rtl' : 'ltr'}>
          <style>{MOCK_CSS}</style>
          <ResultScreen view={view} ar={ar} />
        </div>
      </>
    );
  }

  const qs = view.questions || [];
  const q = qs[Math.min(idx, qs.length - 1)];
  const answeredCount = qs.filter((x) => answers[String(x.id)]).length;
  const unanswered = qs.length - answeredCount;
  const clockClass = timeLeft <= 60 ? 'danger' : timeLeft <= 300 ? 'warn' : '';
  const pct = view.section_limit_seconds ? (timeLeft / view.section_limit_seconds) * 100 : 0;
  const qDir = q && q.language === 'ar' ? 'rtl' : 'ltr';

  return (
    <>
      <Navbar />
      <div className="page mk-wrap">
        <style>{MOCK_CSS}</style>

        <div className="mk-bar" dir={ar ? 'rtl' : 'ltr'}>
          <div className="mk-bar-row">
            <div>
              <div className="mk-section">
                {ar ? `القسم ${view.section_index + 1} من ${view.section_count}` : `Section ${view.section_index + 1} of ${view.section_count}`}
              </div>
              <div className="mk-sub">{view.assignment.title} · {ar ? `السؤال ${idx + 1} من ${qs.length}` : `Question ${idx + 1} of ${qs.length}`}</div>
            </div>
            <div className={`mk-clock ${clockClass}`}>{fmtClock(timeLeft)}</div>
          </div>
          <div className="mk-progress"><div style={{ width: `${pct}%` }} /></div>
          <div className="mk-nav">
            {qs.map((x, i) => (
              <button key={x.id} type="button"
                      className={`mk-dot${answers[String(x.id)] ? ' answered' : ''}${i === idx ? ' current' : ''}`}
                      onClick={() => setIdx(i)}>{i + 1}</button>
            ))}
          </div>
        </div>

        {q ? (
          <div className="mk-card" dir={qDir}>
            {q.image_url ? <div className="mk-figure"><img src={q.image_url} alt="" /></div> : null}
            <div className="mk-q"><MathText text={q.question_text} /></div>
            {LETTERS.map((l) => (
              <div key={l} className={`mk-opt${answers[String(q.id)] === l ? ' selected' : ''}`} onClick={() => choose(q.id, l)}
                   role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') choose(q.id, l); }}>
                <span className="mk-key">{l.toUpperCase()}</span>
                <span className="mk-opt-text"><MathText text={q[`option_${l}`]} /></span>
              </div>
            ))}
          </div>
        ) : null}

        <div className="mk-foot" dir={ar ? 'rtl' : 'ltr'}>
          <button type="button" className="btn btn-ghost" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}>
            {ar ? 'السابق' : 'Previous'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setIdx((i) => Math.min(qs.length - 1, i + 1))} disabled={idx >= qs.length - 1}>
            {ar ? 'التالي' : 'Next'}
          </button>
          <span className="spacer" />
          {unanswered > 0 ? <span className="mk-unanswered">{ar ? `${unanswered} بدون إجابة` : `${unanswered} unanswered`}</span> : null}
          <button type="button" className="btn btn-primary" onClick={() => setConfirmNext(true)} disabled={busy}>
            {view.is_last_section ? (ar ? 'إنهاء وتسليم' : 'Finish and submit') : (ar ? 'إغلاق القسم والانتقال' : 'Close section and continue')}
          </button>
        </div>

        {confirmNext ? (
          <div className="mk-overlay" onClick={() => setConfirmNext(false)}>
            <div className="mk-dialog" dir={ar ? 'rtl' : 'ltr'} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 8px' }}>
                {view.is_last_section ? (ar ? 'تسليم الاختبار؟' : 'Submit the exam?') : (ar ? 'إغلاق هذا القسم؟' : 'Close this section?')}
              </h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 16px' }}>
                {unanswered > 0
                  ? (ar ? `لديك ${unanswered} سؤالًا بدون إجابة. ` : `You have ${unanswered} unanswered question${unanswered === 1 ? '' : 's'}. `)
                  : ''}
                {ar ? 'لا يمكنك الرجوع إلى هذا القسم بعد إغلاقه.' : 'You cannot return to this section once it is closed.'}
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setConfirmNext(false)}>{ar ? 'رجوع' : 'Go back'}</button>
                <button type="button" className="btn btn-primary" onClick={nextSection} disabled={busy}>
                  {view.is_last_section ? (ar ? 'تسليم' : 'Submit') : (ar ? 'إغلاق والانتقال' : 'Close and continue')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
